import React, { useMemo, useState, useEffect } from 'react';
import {
    useReactTable,
    createColumnHelper,
    getCoreRowModel,
    getPaginationRowModel,
    getSortedRowModel,
    getFilteredRowModel,
    flexRender,
} from '@tanstack/react-table';

import api from '../../config/axios';
import AdminMenu from '../../components/admin/menu';

interface Categorie {
    categorieId: number;
    categorieLib: string;
}

const AdminCategorie: React.FC = () => {
    const [data, setData] = useState<Categorie[]>([]);
    const [globalFilter, setGlobalFilter] = useState('');

    // Charger les données depuis l'API
    useEffect(() => {
        const fetchCategories = async () => {
            try {
                const response = await api.get('/categorie');
                setData(response.data); // Remplace les données statiques par celles de l'API
            } catch (error) {
                console.error('Erreur lors du chargement des catégories :', error);
            }
        };

        fetchCategories();
    }, []);

    // Définir les colonnes
    const columnHelper = createColumnHelper<Categorie>();
    const columns = useMemo(
        () => [
            columnHelper.accessor('categorieId', {
                header: 'ID',
                cell: (info) => info.getValue(),
                footer: (info) => info.column.id,
            }),
            columnHelper.accessor('categorieLib', {
                header: 'Nom',
                cell: (info) => info.getValue(),
                footer: (info) => info.column.id,
            }),
            columnHelper.display({
                id: 'actions',
                header: 'Actions',
                cell: ({ row }) => (
                    <div className="flex space-x-2">
                        <button
                            onClick={() => alert(`Modifier ID: ${row.original.categorieId}`)}
                            className="bg-yellow-500 text-white px-2 py-1 rounded-md hover:bg-yellow-400"
                        >
                            Modifier
                        </button>
                        <button
                            onClick={() => handleDelete(row.original.categorieId)}
                            className="bg-red-600 text-white px-2 py-1 rounded-md hover:bg-red-500"
                        >
                            Supprimer
                        </button>
                    </div>
                ),
            }),
        ],
        [columnHelper]
    );

    // Supprimer une catégorie
    const handleDelete = async (id: number) => {
        if (window.confirm('Êtes-vous sûr de vouloir supprimer cette catégorie ?')) {
            try {
                await api.delete(`/categorie/${id}`);
                setData((prevData) => prevData.filter((categorie) => categorie.categorieId !== id));
            } catch (error) {
                console.error('Erreur lors de la suppression :', error);
            }
        }
    };

    // Initialiser la table
    const table = useReactTable({
        data,
        columns,
        state: {
            globalFilter,
        },
        onGlobalFilterChange: setGlobalFilter,
        getCoreRowModel: getCoreRowModel(),
        getPaginationRowModel: getPaginationRowModel(),
        getSortedRowModel: getSortedRowModel(),
        getFilteredRowModel: getFilteredRowModel(),
        initialState: {
            pagination: {
                pageSize: 10,
            },
        },
    });

    return (
        <>
        <div className="flex h-screen bg-gray-100">
            <AdminMenu />
            <main className="flex-1 p-6 bg-white shadow-md rounded-md">
                <h1 className="text-2xl font-bold mb-4">Gestion des Catégories</h1>

                {/* Filtrage global */}
                <input
                    type="text"
                    value={globalFilter || ''}
                    onChange={(e) => setGlobalFilter(e.target.value)}
                    placeholder="Rechercher..."
                    className="mb-4 p-2 border rounded-md"
                />

                {/* Table */}
                <table className="table-auto w-full border-collapse border border-gray-200">
                    <thead>
                        {table.getHeaderGroups().map((headerGroup) => (
                            <tr key={headerGroup.id}>
                                {headerGroup.headers.map((header) => (
                                    <th key={header.id} className="border px-4 py-2 bg-gray-100">
                                        {flexRender(header.column.columnDef.header, header.getContext())}
                                        {/* Ajout de tri */}
                                        {header.column.getCanSort() && (
                                            <button
                                                onClick={header.column.getToggleSortingHandler()}
                                                className="ml-2 text-blue-500"
                                            >
                                                {header.column.getIsSorted()
                                                    ? header.column.getIsSorted() === 'asc'
                                                        ? '🔼'
                                                        : '🔽'
                                                    : '⇅'}
                                            </button>
                                        )}
                                    </th>
                                ))}
                            </tr>
                        ))}
                    </thead>
                    <tbody>
                        {table.getRowModel().rows.map((row) => (
                            <tr key={row.id}>
                                {row.getVisibleCells().map((cell) => (
                                    <td key={cell.id} className="border px-4 py-2">
                                        {flexRender(cell.column.columnDef.cell, cell.getContext())}
                                    </td>
                                ))}
                            </tr>
                        ))}
                    </tbody>
                    <tfoot>
                        {table.getFooterGroups().map((footerGroup) => (
                            <tr key={footerGroup.id}>
                                {footerGroup.headers.map((header) => (
                                    <td key={header.id} className="border px-4 py-2">
                                        {flexRender(header.column.columnDef.footer, header.getContext())}
                                    </td>
                                ))}
                            </tr>
                        ))}
                    </tfoot>
                </table>

                {/* Pagination */}
                <div className="flex items-center justify-between mt-4">
                    <button
                        onClick={() => table.previousPage()}
                        disabled={!table.getCanPreviousPage()}
                        className="bg-gray-200 px-4 py-2 rounded-md disabled:opacity-50"
                    >
                        Précédent
                    </button>
                    <span>
                        Page{' '}
                        <strong>
                            {table.getState().pagination.pageIndex + 1} sur {table.getPageCount()}
                        </strong>
                    </span>
                    <button
                        onClick={() => table.nextPage()}
                        disabled={!table.getCanNextPage()}
                        className="bg-gray-200 px-4 py-2 rounded-md disabled:opacity-50"
                    >
                        Suivant
                    </button>
                </div>
            </main>
        </div>
        </>
    );
};

export default AdminCategorie;
